import React, { createContext, useState, useEffect, useContext } from "react";
import axiosInstance from "../api/axiosInstance";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);

  const [loading, setLoading] = useState(true); // app startup restore
  const [authReady, setAuthReady] = useState(false); // token ready for API

  // ✅ Restore auth once on app load
  // useEffect(() => {
  //   try {
  //     const storedUser = localStorage.getItem("user");
  //     const storedToken = localStorage.getItem("authToken");

  //     if (storedUser && storedToken) {
  //       setUser(JSON.parse(storedUser));
  //       setToken(storedToken);
  //       window.__APP_AUTH__ = { token: storedToken };
  //       setAuthReady(true);
  //     } else {
  //       window.__APP_AUTH__ = {};
  //       setAuthReady(false);
  //     }
  //   } catch (err) {
  //     console.error("Auth restore failed:", err);
  //     localStorage.removeItem("user");
  //     localStorage.removeItem("authToken");
  //     window.__APP_AUTH__ = {};
  //     setUser(null);
  //     setToken(null);
  //     setAuthReady(false);
  //   } finally {
  //     setLoading(false);
  //   }
  // }, []);

  useEffect(() => {
    const restoreAuth = async () => {
      try {
        const storedUser = localStorage.getItem("user");
        const storedToken = localStorage.getItem("authToken");

        if (!storedUser || !storedToken) {
          setLoading(false);
          return;
        }

        await axiosInstance.get("/api/auth/validate", {
          headers: {
            Authorization: `Bearer ${storedToken}`,
          },
        });
        setUser(JSON.parse(storedUser));
        setToken(storedToken);

        window.__APP_AUTH__ = { token: storedToken };
        setAuthReady(true);
      } catch (error) {
        const message = error?.response?.data?.message || "Session Expired";

        alert(message);

        localStorage.removeItem("user");
        localStorage.removeItem("authToken");

        window.__APP_AUTH__ = {};

        setUser(null);
        setToken(null);
        setAuthReady(false);
      } finally {
        setLoading(false);
      }
    };
    
    restoreAuth();
  }, []);

  // ✅ Keep global token updated for axios
  useEffect(() => {
    if (token) {
      window.__APP_AUTH__ = { token };
      setAuthReady(true);
    } else {
      window.__APP_AUTH__ = {};
      setAuthReady(false);
    }
  }, [token]);

  // ✅ Cross-tab sync (important!)
  useEffect(() => {
    const sync = (event) => {
      if (event.key === "authToken") {
        setToken(event.newValue);
      }
      if (event.key === "user") {
        setUser(event.newValue ? JSON.parse(event.newValue) : null);
      }
    };

    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);

  const login = (userData, authToken) => {
    // ✅ Always set token first so axios can use it immediately
    localStorage.setItem("authToken", authToken);
    window.__APP_AUTH__ = { token: authToken };
    setToken(authToken);

    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("authToken");

    window.__APP_AUTH__ = {};
    setUser(null);
    setToken(null);
    setAuthReady(false);
  };

  // ✅ final auth state (most stable)
  const isAuthenticated = authReady && !!user;

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        authReady,
        isAuthenticated,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
